/**
 * Templates Page - Repair Template Management
 * 
 * Allows users to create and manage repair templates.
 * Templates define custom Basic Repair Fields that can be used when creating repair tickets.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { Plus, Edit2, Trash2, Save, X, DollarSign, List } from 'lucide-react';
import api from '../../../lib/api';

export default function Templates() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Template form state
  const [templateForm, setTemplateForm] = useState({
    template_name: '',
    checklist_items: [] // Array of { id, label, cost }
  });
  
  // Checklist item being edited
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    id: '',
    label: '',
    cost: 0
  });

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/inspection-templates');
      setTemplates(response);
    } catch (error) {
      console.error('Failed to load templates:', error);
      alert('Failed to load templates: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      template_name: '',
      checklist_items: []
    });
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      template_name: template.template_name,
      checklist_items: template.checklist_items || []
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/api/inspection-templates/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.template_name.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (templateForm.checklist_items.length === 0) {
      alert('Please add at least one checklist item');
      return;
    }

    try {
      setSaving(true);
      
      if (editingTemplate) {
        // Update existing template
        const response = await api.put(`/api/inspection-templates/${editingTemplate.id}`, templateForm);
        setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? response : t));
      } else {
        // Create new template
        const response = await api.post('/api/inspection-templates', templateForm);
        setTemplates(prev => [...prev, response]);
      }
      
      setShowTemplateModal(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddChecklistItem = () => {
    if (!itemForm.label.trim()) {
      alert('Please enter an item label');
      return;
    }

    const newItem = {
      id: Date.now().toString(),
      label: itemForm.label,
      cost: parseFloat(itemForm.cost) || 0
    };

    setTemplateForm(prev => ({
      ...prev,
      checklist_items: [...prev.checklist_items, newItem]
    }));

    // Reset item form
    setItemForm({
      id: '',
      label: '',
      cost: 0
    });
  };

  const handleUpdateChecklistItem = () => {
    if (!itemForm.label.trim()) {
      alert('Please enter an item label');
      return;
    }

    setTemplateForm(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.map(item =>
        item.id === editingItem.id
          ? {
              ...item,
              label: itemForm.label,
              cost: parseFloat(itemForm.cost) || 0
            }
          : item
      )
    }));

    setEditingItem(null);
    setItemForm({
      id: '',
      label: '',
      cost: 0
    });
  };

  const handleRemoveChecklistItem = (itemId) => {
    setTemplateForm(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.filter(item => item.id !== itemId)
    }));
  };

  const handleEditChecklistItem = (item) => {
    setEditingItem(item);
    setItemForm({
      id: item.id,
      label: item.label,
      cost: item.cost || 0
    });
  };

  const handleCancelEditItem = () => {
    setEditingItem(null);
    setItemForm({
      id: '',
      label: '',
      cost: 0
    });
  };

  const calculateTotalCost = () => {
    return templateForm.checklist_items.reduce(
      (sum, item) => sum + (parseFloat(item.cost) || 0),
      0
    );
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p>Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-[calc(100vh-8rem)] flex flex-col ${isDark ? 'bg-slate-900 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
      {/* Tab Navigation */}
      <div className={`flex gap-1 px-4 sm:px-6 pt-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <button
          onClick={() => navigate('/workflows')}
          className={`px-4 py-2.5 font-medium text-sm rounded-t-lg transition-colors ${
            location.pathname === '/workflows'
              ? isDark
                ? 'bg-slate-800 text-white border-b-2 border-blue-500'
                : 'bg-white text-slate-900 border-b-2 border-blue-500'
              : isDark
                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          Repairs
        </button>
        <button
          onClick={() => navigate('/workflows/templates')}
          className={`px-4 py-2.5 font-medium text-sm rounded-t-lg transition-colors ${
            location.pathname === '/workflows/templates'
              ? isDark
                ? 'bg-slate-800 text-white border-b-2 border-blue-500'
                : 'bg-white text-slate-900 border-b-2 border-blue-500'
              : isDark
                ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          Templates
        </button>
      </div>
      
      {/* Header */}
      <div className="flex justify-between items-center mb-6 px-6 pt-6">
        <div>
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Repair Templates
          </h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Create custom Basic Repair Fields templates
          </p>
        </div>
        <button
          onClick={handleAddNewTemplate}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
        >
          <Plus size={18} />
          New Template
        </button>
      </div>

      {/* Templates List */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {templates.length === 0 ? (
          <div className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            <List size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg mb-2">No templates yet</p>
            <p className="text-sm">Create your first repair template to get started</p>
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {templates.map(template => (
              <div
                key={template.id}
                className={`rounded-xl border p-5 ${
                  isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className={`font-semibold text-lg mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {template.template_name}
                    </h3>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      }`}
                      title="Edit template"
                    >
                      <Edit2 size={16} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDark ? 'hover:bg-red-900/30' : 'hover:bg-red-50'
                      }`}
                      title="Delete template"
                    >
                      <Trash2 size={16} className="text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Template Details */}
                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2 text-sm">
                    <List size={14} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
                    <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                      {template.checklist_items?.length || 0} checklist items
                    </span>
                  </div>
                </div>

                {/* Checklist Items Preview */}
                {template.checklist_items && template.checklist_items.length > 0 && (
                  <div className={`pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="space-y-1">
                      {template.checklist_items.slice(0, 3).map(item => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>{item.label}</span>
                          <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>
                            ${parseFloat(item.cost || 0).toFixed(2)}
                          </span>
                        </div>
                      ))}
                      {template.checklist_items.length > 3 && (
                        <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                          +{template.checklist_items.length - 3} more...
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Total Cost */}
                <div className={`pt-3 mt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      Total Default Cost:
                    </span>
                    <span className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      ${(template.checklist_items || []).reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden ${
            isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          } shadow-2xl`}>
            {/* Modal Header */}
            <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h3>
                <button
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <X size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-10rem)]">
              <div className="space-y-6">
                {/* Template Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={templateForm.template_name}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, template_name: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-900 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-slate-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="e.g., Standard Pallet Inspection"
                  />
                </div>

                {/* Checklist Items */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Basic Repair Fields *
                  </label>

                  {/* Existing Items */}
                  {templateForm.checklist_items.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {templateForm.checklist_items.map((item, index) => (
                        <div
                          key={item.id}
                          className={`p-3 rounded-lg border ${
                            isDark ? 'bg-gray-900/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {item.label}
                              </span>
                              <span className={`ml-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                ${parseFloat(item.cost || 0).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => handleEditChecklistItem(item)}
                                className={`p-1.5 rounded transition-colors ${
                                  isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                                }`}
                                title="Edit item"
                              >
                                <Edit2 size={14} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveChecklistItem(item.id)}
                                className={`p-1.5 rounded transition-colors ${
                                  isDark ? 'hover:bg-red-900/30' : 'hover:bg-red-50'
                                }`}
                                title="Remove item"
                              >
                                <Trash2 size={14} className="text-red-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add/Edit Item Form */}
                  <div className={`p-4 rounded-lg border ${
                    isDark ? 'bg-gray-900/30 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                      {editingItem ? 'Edit Item' : 'Add New Item'}
                    </h4>
                    
                    <div className="space-y-3">
                      {/* Item Label */}
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                          Item Label *
                        </label>
                        <input
                          type="text"
                          value={itemForm.label}
                          onChange={(e) => setItemForm(prev => ({ ...prev, label: e.target.value }))}
                          className={`w-full px-3 py-2 text-sm rounded-lg border ${
                            isDark 
                              ? 'bg-gray-800 border-gray-600 text-white' 
                              : 'bg-white border-gray-300 text-slate-900'
                          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          placeholder="e.g., Base, Door 1, Corner 2"
                        />
                      </div>

                      {/* Cost */}
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                          Repair Cost
                        </label>
                        <div className="relative">
                          <DollarSign size={14} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                          <input
                            type="number"
                            step="0.01"
                            value={itemForm.cost}
                            onChange={(e) => setItemForm(prev => ({ ...prev, cost: e.target.value }))}
                            className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg border ${
                              isDark 
                                ? 'bg-gray-800 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-slate-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2">
                        {editingItem ? (
                          <>
                            <button
                              type="button"
                              onClick={handleCancelEditItem}
                              className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                                isDark 
                                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                              }`}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleUpdateChecklistItem}
                              className="flex-1 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              <Save size={14} />
                              Update Item
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={handleAddChecklistItem}
                            className="w-full px-3 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                          >
                            <Plus size={14} />
                            Add Item
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total Cost Display */}
                {templateForm.checklist_items.length > 0 && (
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-emerald-50 border border-emerald-200'}`}>
                    <div className="flex justify-between items-center">
                      <span className={`font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                        Total Default Cost:
                      </span>
                      <span className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        ${calculateTotalCost().toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex gap-3`}>
              <button
                type="button"
                onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                }}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  isDark 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
